import { useState, useEffect } from 'react';
import type { Contract, ContractTemplate, Payment, Deliverable, Milestone } from '@shared/types';
import { LICENSE_PRODUCTS, LICENSE_TERMS, PAYMENT_MILESTONE_OPTIONS, PROJECT_MILESTONES } from '@shared/constants';
import { generatePaymentSchedule, generateLicenseDescription } from '@shared/paymentUtils';
import { PlusIcon, TrashIcon, CurrencyDollarIcon, BookmarkIcon, FolderOpenIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { SaveScopeDialog } from './SaveScopeDialog';
import { LoadScopeDialog } from './LoadScopeDialog';
import { fetchLightPathPricing, convertLightPathPayments } from '../../services/lightpathPricingService';

interface ScopePricingStepProps {
  data: Partial<Contract>;
  onUpdate: (updates: Partial<Contract>) => void;
  template?: ContractTemplate;
}

export function ScopePricingStep({ data, onUpdate, template }: ScopePricingStepProps) {
  const [deliverables, setDeliverables] = useState<Deliverable[]>(data.deliverables || []);
  
  // Financial state
  const [serviceCost, setServiceCost] = useState<string>(data.serviceCost?.toString() || '');
  const [licenseProduct, setLicenseProduct] = useState(data.licenseProduct || '');
  const [customProduct, setCustomProduct] = useState('');
  const [licenseTerms, setLicenseTerms] = useState(data.licenseTerms || '1-year');
  const [licenseUsers, setLicenseUsers] = useState<string>(data.licenseUsers?.toString() || '');
  const [licensePricingTier, setLicensePricingTier] = useState(data.licensePricingTier || '');
  const [licenseCost, setLicenseCost] = useState<string>(data.licenseCost?.toString() || '');
  const [paymentMilestoneCount, setPaymentMilestoneCount] = useState<2 | 3 | 4>(data.paymentMilestoneCount || 3);
  const [useCustomPayments, setUseCustomPayments] = useState(data.useCustomPayments || false);
  
  // Auto-generated payments preview
  const [previewPayments, setPreviewPayments] = useState<Payment[]>([]);
  
  // Scope management dialogs
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [showLoadDialog, setShowLoadDialog] = useState(false);
  
  // LightPath pricing integration
  const [fetchingLightPathPrice, setFetchingLightPathPrice] = useState(false);

  // Sync deliverables to parent
  useEffect(() => {
    onUpdate({ deliverables });
  }, [deliverables]);

  // LightPath Pricing API integration
  useEffect(() => {
    const isLightPath = licenseProduct === 'LightPath Intranet';
    const hasUsers = licenseUsers && parseInt(licenseUsers) > 0;
    const hasTerm = licenseTerms;
    
    if (isLightPath && hasUsers && hasTerm && !fetchingLightPathPrice) {
      const fetchPricing = async () => {
        setFetchingLightPathPrice(true);
        const toastId = toast.loading('⚡ Fetching LightPath pricing...');
        
        try {
          const result = await fetchLightPathPricing(
            parseInt(licenseUsers),
            licenseTerms,
            data.clientName
          );
          
          if (result.success && result.data) {
            if (result.data.contactUs) {
              // Enterprise tier - show message
              toast('💼 Enterprise pricing - contact sales', { 
                id: toastId,
                icon: '💼',
                duration: 5000,
              });
              toast('For 50,000+ users, please contact sales for custom pricing');
            } else {
              // Populate pricing data
              setServiceCost(result.data.implementation.toString());
              setLicenseCost(result.data.annualLicense.toString());
              
              // Convert and set payment schedule
              const payments = convertLightPathPayments(result.data.paymentSchedule);
              setPreviewPayments(payments);
              setUseCustomPayments(true); // Use API payments instead of auto-generated
              
              toast.success(
                `✅ Pricing loaded: $${result.data.implementation.toLocaleString()} services + $${result.data.annualLicense.toLocaleString()} license`,
                { id: toastId, duration: 4000 }
              );
              
              // Update parent with all data
              const licenseDesc = generateLicenseDescription(
                'LightPath Intranet',
                licenseTerms,
                parseInt(licenseUsers),
                result.data.tier.tierName
              );
              
              onUpdate({
                serviceCost: result.data.implementation,
                licenseCost: result.data.annualLicense,
                licenseProduct: 'LightPath Intranet',
                licenseTerms,
                licenseUsers: parseInt(licenseUsers),
                licensePricingTier: result.data.tier.tierName,
                licenseDescription: licenseDesc,
                paymentMilestoneCount: 3,
                useCustomPayments: true,
                payments,
              });
            }
          } else {
            toast.error(result.error || 'Failed to fetch pricing', { id: toastId });
          }
        } catch (error) {
          console.error('Error fetching LightPath pricing:', error);
          toast.error('Failed to fetch pricing. Please enter manually.', { id: toastId });
        } finally {
          setFetchingLightPathPrice(false);
        }
      };
      
      // Debounce API calls - wait 500ms after user stops typing
      const timeoutId = setTimeout(fetchPricing, 500);
      return () => clearTimeout(timeoutId);
    }
  }, [licenseProduct, licenseUsers, licenseTerms, data.clientName]);

  // Auto-generate payments when financial data changes
  useEffect(() => {
    if (!useCustomPayments && serviceCost) {
      const service = parseFloat(serviceCost) || 0;
      const license = parseFloat(licenseCost) || 0;
      
      if (service > 0) {
        const payments = generatePaymentSchedule({
          serviceCost: service,
          licenseCost: license,
          milestoneCount: paymentMilestoneCount,
        });
        setPreviewPayments(payments);
        
        // Update parent with financial data and payments
        const product = licenseProduct === 'Custom' ? customProduct : licenseProduct;
        const licenseDesc = product && licenseTerms 
          ? generateLicenseDescription(product, licenseTerms, parseInt(licenseUsers) || undefined, licensePricingTier || undefined)
          : '';
        
        onUpdate({
          serviceCost: service,
          licenseCost: license > 0 ? license : undefined,
          licenseProduct: product || undefined,
          licenseTerms: product ? licenseTerms : undefined,
          licenseUsers: licenseUsers ? parseInt(licenseUsers) : undefined,
          licensePricingTier: licensePricingTier || undefined,
          licenseDescription: licenseDesc || undefined,
          paymentMilestoneCount,
          useCustomPayments,
          payments,
        });
      }
    }
  }, [serviceCost, licenseCost, licenseProduct, customProduct, licenseTerms, licenseUsers, licensePricingTier, paymentMilestoneCount, useCustomPayments]);

  const addDeliverable = () => {
    const newDeliverable: Deliverable = {
      id: crypto.randomUUID(),
      title: '',
      description: '',
    };
    setDeliverables([...deliverables, newDeliverable]);
  };

  const updateDeliverable = (id: string, field: keyof Deliverable, value: string) => {
    setDeliverables(deliverables.map((d) => (d.id === id ? { ...d, [field]: value } : d)));
  };

  const removeDeliverable = (id: string) => {
    setDeliverables(deliverables.filter((d) => d.id !== id));
  };

  const handleLoadScope = (loadedDeliverables: Deliverable[]) => {
    setDeliverables(loadedDeliverables);
  };

  const formatCurrency = (value: number) => {
    return value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  return (
    <div className="space-y-8">
      {/* Deliverables Section */}
      {template?.hasDeliverables && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Project Deliverables</h3>
              <p className="text-sm text-gray-600">Define the key deliverables for this engagement</p>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setShowLoadDialog(true)}
                className="inline-flex items-center px-3 py-2 border border-green-300 text-sm font-medium rounded-md text-green-700 bg-white hover:bg-green-50"
              >
                <FolderOpenIcon className="h-4 w-4 mr-1" />
                Load Scope
              </button>
              <button
                type="button"
                onClick={() => setShowSaveDialog(true)}
                disabled={deliverables.length === 0}
                className="inline-flex items-center px-3 py-2 border border-blue-300 text-sm font-medium rounded-md text-blue-700 bg-white hover:bg-blue-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <BookmarkIcon className="h-4 w-4 mr-1" />
                Save Scope
              </button>
              <button
                type="button"
                onClick={addDeliverable}
                className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700"
              >
                <PlusIcon className="h-4 w-4 mr-1" />
                Add Deliverable
              </button>
            </div>
          </div>

          <div className="space-y-4">
            {deliverables.length === 0 ? (
              <div className="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                <p className="text-gray-500">No deliverables added yet. Click "Add Deliverable" to get started.</p>
              </div>
            ) : (
              deliverables.map((deliverable, index) => (
                <div key={deliverable.id} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                  <div className="flex items-start justify-between mb-3">
                    <h4 className="text-sm font-medium text-gray-700">Deliverable {index + 1}</h4>
                    <button
                      type="button"
                      onClick={() => removeDeliverable(deliverable.id)}
                      className="text-red-600 hover:text-red-800"
                    >
                      <TrashIcon className="h-5 w-5" />
                    </button>
                  </div>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
                      <input
                        type="text"
                        value={deliverable.title}
                        onChange={(e) => updateDeliverable(deliverable.id, 'title', e.target.value)}
                        placeholder="e.g., Phase 1: Discovery & Planning"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Description *</label>
                      <textarea
                        value={deliverable.description}
                        onChange={(e) => updateDeliverable(deliverable.id, 'description', e.target.value)}
                        placeholder="Describe what will be delivered..."
                        rows={3}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                        required
                      />
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* Financial Section */}
      <div className="border-t pt-8">
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center">
            <CurrencyDollarIcon className="h-5 w-5 mr-2 text-primary-600" />
            Financial Breakdown
          </h3>
          <p className="text-sm text-gray-600 mt-1">Enter service and licensing costs to auto-generate payment schedule</p>
        </div>

        <div className="space-y-6">
          {/* Service Cost */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Professional Services Cost * 
              {licenseProduct === 'LightPath Intranet' && serviceCost && (
                <span className="text-xs text-green-600 ml-2">⚡ Auto-calculated (editable)</span>
              )}
            </label>
            <div className="relative">
              <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-gray-500">$</span>
              <input
                type="number"
                value={serviceCost}
                onChange={(e) => setServiceCost(e.target.value)}
                placeholder="45000"
                step="0.01"
                min="0"
                className="w-full pl-7 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                required
              />
            </div>
            {licenseProduct === 'LightPath Intranet' && serviceCost && (
              <p className="mt-1 text-xs text-gray-500">
                Auto-calculated from API. You can adjust this value as needed.
              </p>
            )}
          </div>

          {/* Licensing Section (conditional) */}
          {template?.hasLicensing && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-4">
              <div className="flex items-center justify-between">
                <h4 className="font-medium text-gray-900">Product Licensing</h4>
                {fetchingLightPathPrice && (
                  <span className="flex items-center text-sm text-blue-600 animate-pulse">
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Fetching pricing...
                  </span>
                )}
              </div>
              
              {licenseProduct === 'LightPath Intranet' && !fetchingLightPathPrice && licenseUsers && (
                <div className="bg-green-50 border border-green-200 rounded p-3">
                  <p className="text-sm text-green-800">
                    ⚡ <strong>Auto-Pricing Enabled:</strong> Pricing will update automatically as you enter users and term.
                  </p>
                </div>
              )}
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Product</label>
                  <select
                    value={licenseProduct}
                    onChange={(e) => setLicenseProduct(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white"
                  >
                    <option value="">Select product...</option>
                    {LICENSE_PRODUCTS.map(product => (
                      <option key={product} value={product}>{product}</option>
                    ))}
                  </select>
                </div>

                {licenseProduct === 'Custom' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Custom Product Name</label>
                    <input
                      type="text"
                      value={customProduct}
                      onChange={(e) => setCustomProduct(e.target.value)}
                      placeholder="Enter product name"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Contract Term</label>
                  <select
                    value={licenseTerms}
                    onChange={(e) => setLicenseTerms(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white"
                    disabled={!licenseProduct}
                  >
                    {LICENSE_TERMS.map(term => (
                      <option key={term} value={term}>{term}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Number of Users</label>
                  <input
                    type="number"
                    value={licenseUsers}
                    onChange={(e) => setLicenseUsers(e.target.value)}
                    placeholder="50"
                    min="1"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    disabled={!licenseProduct}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    OR Pricing Tier
                    {licenseProduct === 'LightPath Intranet' && licensePricingTier && (
                      <span className="text-xs text-green-600 ml-2">⚡ Auto-detected (editable)</span>
                    )}
                  </label>
                  <input
                    type="text"
                    value={licensePricingTier}
                    onChange={(e) => setLicensePricingTier(e.target.value)}
                    placeholder="e.g., Enterprise"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    disabled={!licenseProduct}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Annual License Cost
                  {licenseProduct === 'LightPath Intranet' && licenseCost && (
                    <span className="text-xs text-green-600 ml-2">⚡ Auto-calculated (editable)</span>
                  )}
                </label>
                <div className="relative">
                  <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-gray-500">$</span>
                  <input
                    type="number"
                    value={licenseCost}
                    onChange={(e) => setLicenseCost(e.target.value)}
                    placeholder="5000"
                    step="0.01"
                    min="0"
                    className="w-full pl-7 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    disabled={!licenseProduct}
                  />
                </div>
                {licenseProduct === 'LightPath Intranet' && licenseCost && (
                  <p className="mt-1 text-xs text-gray-500">
                    Auto-calculated from API. You can adjust this value as needed.
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Payment Milestone Configuration */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Payment Milestones</label>
            <div className="flex gap-3">
              {PAYMENT_MILESTONE_OPTIONS.map(option => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => setPaymentMilestoneCount(option.value as 2 | 3 | 4)}
                  className={`px-4 py-2 border rounded-md text-sm font-medium transition-colors ${
                    paymentMilestoneCount === option.value
                      ? 'bg-primary-600 text-white border-primary-600'
                      : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          {/* Payment Schedule Preview */}
          {serviceCost && previewPayments.length > 0 && (
            <div className="bg-gray-50 border border-gray-300 rounded-lg p-4">
              <h4 className="font-medium text-gray-900 mb-3">Payment Schedule Preview</h4>
              <div className="space-y-2">
                {previewPayments.map((payment, index) => (
                  <div key={index} className="flex justify-between items-center text-sm">
                    <div>
                      <span className="font-medium text-gray-900">{payment.milestone}</span>
                      <span className="text-gray-500 ml-2">• {payment.description}</span>
                    </div>
                    <div className="text-right">
                      <div className="font-semibold text-gray-900">${formatCurrency(payment.amount)}</div>
                      {payment.serviceAmount !== undefined && payment.licenseAmount !== undefined && (
                        <div className="text-xs text-gray-500">
                          Services: ${formatCurrency(payment.serviceAmount)} + 
                          Licenses: ${formatCurrency(payment.licenseAmount)}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
                <div className="border-t pt-2 mt-2 flex justify-between font-bold text-gray-900">
                  <span>TOTAL</span>
                  <span>${formatCurrency(previewPayments.reduce((sum, p) => sum + p.amount, 0))}</span>
                </div>
              </div>
            </div>
          )}

          {/* Custom Payments Toggle */}
          <div className="flex items-center">
            <input
              type="checkbox"
              checked={useCustomPayments}
              onChange={(e) => setUseCustomPayments(e.target.checked)}
              className="h-4 w-4 text-primary-600 rounded"
            />
            <label className="ml-2 text-sm text-gray-700">
              Use custom payment schedule (manual entry)
            </label>
          </div>
        </div>
      </div>

      {/* Scope Management Dialogs */}
      <SaveScopeDialog
        isOpen={showSaveDialog}
        onClose={() => setShowSaveDialog(false)}
        deliverables={deliverables}
        onSaved={() => toast.success('Scope saved! You can now load it in future contracts.')}
      />

      <LoadScopeDialog
        isOpen={showLoadDialog}
        onClose={() => setShowLoadDialog(false)}
        onLoad={handleLoadScope}
      />
    </div>
  );
}

